import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Icons } from '../components/common';
import { API_BASE } from '../constants/urls';
import '../components/admin/AdminDashboard.css'; // Reuse existing dashboard styles

const AssignedArticlesPage = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterText, setFilterText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');

    // For Re-assignment
    const [showReassignModal, setShowReassignModal] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [availableTes, setAvailableTes] = useState([]);
    const [availableCes, setAvailableCes] = useState([]);
    const [reassignUserId, setReassignUserId] = useState('');
    const [reassignStatus, setReassignStatus] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchArticles = useCallback(async (page, size, search, status, role, user) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page,
                per_page: size,
                search: search,
                status: status,
                role: role,
                user: user
            });

            const response = await fetch(`${API_BASE}/api/admin/assigned-articles?${params}`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setArticles(data.articles);
                setTotalRows(data.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching assigned articles:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAvailableUsers = async () => {
        try {
            const [tesRes, cesRes] = await Promise.all([
                fetch(`${API_BASE}/api/te-assignments/available-tes`, { credentials: 'include' }),
                fetch(`${API_BASE}/api/ce-assignments/available-ces`, { credentials: 'include' })
            ]);

            if (tesRes.ok) {
                const data = await tesRes.json();
                setAvailableTes(data.tes || []);
            }
            if (cesRes.ok) {
                const data = await cesRes.json();
                setAvailableCes(data.ces || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    useEffect(() => {
        fetchArticles(currentPage, perPage, filterText, statusFilter, roleFilter, userFilter);
    }, [currentPage, perPage, filterText, statusFilter, roleFilter, userFilter, fetchArticles]);

    useEffect(() => {
        fetchAvailableUsers();
    }, []);

    const handlePageChange = page => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleUpdateStatus = async (articleDbId, newStatus) => {
        // if (!window.confirm(`Are you sure you want to mark this article as ${newStatus}?`)) return;

        try {
            const response = await fetch(`${API_BASE}/api/admin/articles/${articleDbId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
                credentials: 'include'
            });

            if (response.ok) {
                fetchArticles(currentPage, perPage, filterText, statusFilter, roleFilter, userFilter);
            } else {
                const error = await response.json();
                alert(`Error: ${error.error}`);
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleReassign = (article) => {
        setSelectedArticle(article);
        setReassignUserId(article.assignedToId || '');
        setReassignStatus(article.status);
        setShowReassignModal(true);
    };

    const submitReassign = async () => {
        if (!reassignUserId) {
            alert('Please select a user');
            return;
        }
        setSubmitting(true);
        try {
            const response = await fetch(`${API_BASE}/api/admin/articles/${selectedArticle.id}/reassign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: reassignUserId,
                    status: reassignStatus
                }),
                credentials: 'include'
            });

            if (response.ok) {
                setShowReassignModal(false);
                fetchArticles(currentPage, perPage, filterText, statusFilter, roleFilter, userFilter);
            } else {
                const error = await response.json();
                alert(`Error: ${error.error}`);
            }
        } catch (error) {
            console.error('Error reassigning:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            name: 'Article ID',
            selector: row => row.articleId,
            sortable: true,
            cell: row => (
                <div className="font-bold text-gray-900">{row.articleId}</div>
            ),
            width: '180px'
        },
        {
            name: 'Journal',
            selector: row => row.journalName,
            sortable: true,
            cell: row => (
                <div className="text-sm text-gray-600 truncate max-w-[200px]" title={row.journalName}>
                    {row.journalName}
                </div>
            )
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => {
                const statusColors = {
                    'assigned_TE': 'bg-blue-100 text-blue-800',
                    'assigned_CE': 'bg-purple-100 text-purple-800',
                    'TE Submitted': 'bg-green-100 text-green-800',
                    'CE Submitted': 'bg-indigo-100 text-indigo-800',
                    'completed': 'bg-gray-100 text-gray-800',
                    'assigned': 'bg-cyan-100 text-cyan-800'
                };
                return (
                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${statusColors[row.status] || 'bg-gray-100 text-gray-800'}`}>
                        {row.status}
                    </span>
                );
            },
            width: '140px'
        },
        {
            name: 'Assigned To',
            selector: row => row.assignedTo,
            cell: row => (
                <div>
                    <div className="font-semibold text-gray-900">{row.assignedTo || 'Unassigned'}</div>
                    <div className="text-xs text-gray-500 uppercase">{row.assignedToRole}</div>
                </div>
            ),
            width: '150px'
        },
        {
            name: 'Last Updated',
            selector: row => row.updatedAt,
            sortable: true,
            cell: row => row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('en-GB') : 'N/A',
            width: '120px'
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleReassign(row)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg title='Re-assign'"
                    >
                        <Icons.UsersIcon className="w-4 h-4" />
                    </button>
                    {row.status !== 'completed' && (
                        <button
                            onClick={() => handleUpdateStatus(row.id, 'completed')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Mark Completed"
                        >
                            <Icons.CheckIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            ),
            width: '100px'
        }
    ];

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Assigned Articles</h1>
                        <p className="text-gray-600 mt-1">Manage and monitor active article assignments</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-gray-200">
                        <Icons.SearchIcon className="w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search articles..."
                            className="outline-none text-sm w-48"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                        />
                    </div>

                    <select
                        className="bg-white px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="">All Roles</option>
                        <option value="TE">Technical Editor (TE)</option>
                        <option value="CE">Copy Editor (CE)</option>
                    </select>

                    <select
                        className="bg-white px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="assigned">Assigned</option>
                        <option value="assigned_TE">Assigned TE</option>
                        <option value="assigned_CE">Assigned CE</option>
                        <option value="TE Submitted">TE Submitted</option>
                        <option value="CE Submitted">CE Submitted</option>
                    </select>

                    <select
                        className="bg-white px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none"
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                    >
                        <option value="">All Users</option>
                        <optgroup label="Technical Editors (TE)">
                            {availableTes.map(te => (
                                <option key={`te-${te.id}`} value={te.id}>{te.username}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Copy Editors (CE)">
                            {availableCes.map(ce => (
                                <option key={`ce-${ce.id}`} value={ce.id}>{ce.username}</option>
                            ))}
                        </optgroup>
                    </select>

                    <button
                        onClick={() => {
                            setFilterText('');
                            setStatusFilter('');
                            setRoleFilter('');
                            setUserFilter('');
                            setCurrentPage(1);
                        }}
                        className="text-sm text-red-600 font-medium hover:underline"
                    >
                        Clear Filters
                    </button>
                </div>

                <DataTable
                    columns={columns}
                    data={articles}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    highlightOnHover
                    noDataComponent={<div className="p-8 text-gray-500">No assigned articles found</div>}
                />
            </div>

            {/* Re-assignment Modal */}
            {showReassignModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-900">Reassign Article</h3>
                            <button onClick={() => setShowReassignModal(false)} className="p-2 hover:bg-gray-200 rounded-full">
                                <Icons.XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="p-3 bg-blue-50 rounded-xl">
                                <div className="text-sm font-bold text-blue-900">{selectedArticle?.articleId}</div>
                                <div className="text-xs text-blue-700">{selectedArticle?.journalName}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
                                <select
                                    className="w-full bg-white px-4 py-2 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
                                    value={reassignUserId}
                                    onChange={(e) => setReassignUserId(e.target.value)}
                                >
                                    <option value="">Select User...</option>
                                    <optgroup label="Technical Editors (TE)">
                                        {availableTes.map(te => (
                                            <option key={te.id} value={te.id}>{te.username}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Copy Editors (CE)">
                                        {availableCes.map(ce => (
                                            <option key={ce.id} value={ce.id}>{ce.username}</option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    className="w-full bg-white px-4 py-2 rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
                                    value={reassignStatus}
                                    onChange={(e) => setReassignStatus(e.target.value)}
                                >
                                    <option value="assigned_TE">Assigned TE</option>
                                    <option value="assigned_CE">Assigned CE</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => setShowReassignModal(false)}
                                className="flex-1 px-4 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitReassign}
                                disabled={submitting}
                                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50"
                            >
                                {submitting ? 'Updating...' : 'Confirm Reassign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssignedArticlesPage;
